import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { GraphqlService } from '@/service/graphql.service';
import { Category } from '@/model/category';
import { Observable, of } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { MatChipsModule } from '@angular/material/chips';
import { UserService } from '@/service/user.service';
import { CompanyUser } from '@/model/company';
import { KeycloakService } from 'keycloak-angular';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatIconModule } from '@angular/material/icon';
import { User } from '@/model/user';
@Component({
  selector: 'app-company-onboarding',
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatSelectModule,
    MatButtonModule,
    ReactiveFormsModule,
    CommonModule,
    AsyncPipe,
    MatChipsModule,
    MatIconModule,
  ],
  templateUrl: './company-onboarding.component.html',
  styleUrl: './company-onboarding.component.scss',
  standalone: true,
})
export class CompanyOnboardingComponent implements OnInit {
  companyForm: FormGroup;
  availableCategories: Observable<Category[]> = of([]);
  categories: Category[] = [];
  loading = false;
  user: User | null = null;

  constructor(
    private fb: FormBuilder,
    private graphqlService: GraphqlService,
    private userService: UserService,
    private keycloakService: KeycloakService,
    private router: Router,
    private snackBar: MatSnackBar,
  ) {
    this.companyForm = this.fb.group({
      companyName: ['', [Validators.required]],
      contactPersonName: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      uid: ['', []],
      website: ['', [Validators.required]],
      phone: ['', [Validators.required]],
      address: this.fb.group({
        street: ['', Validators.required],
        city: ['', Validators.required],
        postalCode: ['', [Validators.required]],
      }),
      privacyConsent: [false, Validators.requiredTrue],
      termsConsent: [false, Validators.requiredTrue],
      categories: [[], [Validators.required]],
    });
  }

  ngOnInit(): void {
    this.graphqlService.allCategories().subscribe((categories) => {
      this.categories = categories.filter(
        (category) => category.parentId === null,
      );
      this.availableCategories = of(this.categories);
    });

    this.keycloakService.loadUserProfile().then((profile) => {
      this.companyForm.patchValue({
        email: profile.email,
      });
    });

    this.userService.getUserInfo().subscribe((user) => {
      this.user = user;
      if (user) {
        this.companyForm.patchValue({
          contactPersonName: `${user.firstName ? user.firstName : ''} ${
            user.lastName ? user.lastName : ''
          }`,
        });
      }
    });
  }

  get websiteControl() {
    return this.companyForm.get('website');
  }

  get phoneControl() {
    return this.companyForm.get('phone');
  }

  get companyNameControl() {
    return this.companyForm.get('companyName');
  }

  get contactPersonNameControl() {
    return this.companyForm.get('contactPersonName');
  }

  get emailControl() {
    return this.companyForm.get('email');
  }

  get addressControl() {
    return this.companyForm.get('address') as FormGroup;
  }

  get privacyConsentControl() {
    return this.companyForm.get('privacyConsent');
  }

  get termsConsentControl() {
    return this.companyForm.get('termsConsent');
  }

  get categoriesControl() {
    return this.companyForm.get('categories');
  }

  get uidControl() {
    return this.companyForm.get('uid');
  }

  onSubmit() {
    if (this.companyForm.valid) {
      this.loading = true;
      const company = new CompanyUser({
        companyName: this.companyForm.value.companyName,
        contactPersonName: this.companyForm.value.contactPersonName,
        email: this.companyForm.value.email,
        website: this.companyForm.value.website,
        phone: this.companyForm.value.phone,
        address: {
          streetAndNumber: this.companyForm.value.address.street,
          city: this.companyForm.value.address.city,
          postalCode: this.companyForm.value.address.postalCode,
        },
        categoryIds: this.companyForm.value.categories,
        acceptedPrivacyPolicy: this.companyForm.value.privacyConsent,
        acceptedAgb: this.companyForm.value.termsConsent,
        uid: this.companyForm.value.uid,
        password: this.companyForm.value.password,
      });

      if (!this.user) {
        console.log('Creating user');
        const user: Partial<User> = {
          email: this.companyForm.value.email,
          firstName: this.companyForm.value.contactPersonName,
          zipCode: this.companyForm.value.address.postalCode,
          gender: 'diverse',
          birthYear: '1990',
          acceptedPrivacyPolicy: this.companyForm.value.privacyConsent,
          acceptedAgb: this.companyForm.value.termsConsent,
        };

        this.userService.createUser(user).subscribe({
          next: (user) => {
            this.user = user;
            this.createCompany(company);
          },
        });
      } else {
        this.createCompany(company);
      }
    }
  }

  createCompany(company: CompanyUser) {
    this.userService.createOrUpdateCompany(company).subscribe({
      next: () => {
        this.snackBar
          .open(
            'Firmenregistrierung erfolgreich. Du wirst weitergeleitet ...',
            'Schließen',
            { duration: 1000 },
          )
          .afterDismissed()
          .subscribe(() => {
            this.loading = false;
            this.router.navigate(['/']);
          });
      },
      error: (error) => {
        this.loading = false;
        console.error('Error creating company:', error);
        this.snackBar.open('Fehler beim Erstellen der Firma', 'Schließen', {
          duration: 5000,
        });
      },
    });
  }

  getCategoryName(categoryId: string): string | undefined {
    return this.categories.find((c) => c.id === categoryId)?.name;
  }
}
